import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { CreateVirtualCardScreen } from 'src/app/CreateVirtualCardForm/CreateVirtualCardForm'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import {
  CreditCardDropdownInfiniteScrollProps,
  CreditCardOption,
} from 'src/components/CreditCardDropdown/CreditCardDropdown'
import { CustomVirtualCardAddressFlatFields } from 'src/components/CustomVirtualCardAddressBox/CustomVirtualCardAddressBox'
import { PhysicalVirtualCardAddressFlatFields } from 'src/components/PhysicalVirtualCardBox/PhysicalVirtualCardBox'
import RecipientsTooltip from 'src/components/RecipientsTooltip/RecipientsTooltip'
import { RecurrenceFormValues } from 'src/components/RecurrenceForm/RecurrenceForm'
import {
  UserDropdownInfiniteScrollProps,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import VirtualCardForm, {
  VirtualCardFlatFormValues,
} from 'src/components/VirtualCardForm/VirtualCardForm'
import { ImageNames, VirtualCardTypes } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import Trns from 'src/localization/Trns'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import { UserAssociation } from 'src/models/user.models'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectCreditCardListModelsFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import { PostBulkVirtualCardRequest } from 'src/requests/postBulkVirtualCard.request'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { PostVirtualCardRequest } from 'src/requests/postVirtualCard.request'
import { toCents } from 'src/utils/currency.utils'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { css } from 'styled-components'

interface CreateVirtualCardGuestAccessContentProps {
  className?: string
  userOptions: UserOption[]
  recentUserOptions?: UserOption[]
  isFormDisabled: boolean
  labelCreditCardsAsAccounts: boolean
  creditCardOptions: CreditCardOption[]
  receiptAttachments: ReceiptAttachmentModel[]
  uploadingAttachmentThumbnails: Record<string, string>
  postingVirtualCard: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  uploadAttachments: (attachments: PostReceiptAttachmentRequest[]) => void
  deleteAttachment: (attachmentId: string) => void
  postVirtualCardAndOpenToastAndCloseFlyout: (
    body: Partial<PostVirtualCardRequest>,
  ) => void
  postBulkVirtualCardAndOpenToastAndCloseFlyout: (
    body: Partial<PostBulkVirtualCardRequest>,
  ) => void
  setScreen: (val: CreateVirtualCardScreen) => void
  userInfiniteScrollProps: UserDropdownInfiniteScrollProps
  creditCardInfiniteScrollProps: CreditCardDropdownInfiniteScrollProps
  pageLocation: string
  showSoftAlert?: boolean
}

const CreateVirtualCardGuestAccessContent: React.FC<
  CreateVirtualCardGuestAccessContentProps
> = (props) => {
  const queryParams = useSelector(selectQueryParams)
  const { analytics } = useAnalyticsContext()
  const creditCards = useSelector(selectCreditCardListModelsFromState)
  const user = useSelector(selectUserListModelFromState)
  const guestCardImage = useColorOrGrayscaleImage(ImageNames.CardTypeGuest)

  const onSuccessfulSubmit = (
    flatFields: VirtualCardFlatFormValues,
    expenseDetails: Record<string, string>,
    recurrenceFields: RecurrenceFormValues,
    customAddressFields: CustomVirtualCardAddressFlatFields,
    physicalAddressFields: PhysicalVirtualCardAddressFlatFields,
  ): void => {
    const fields = {
      creditCardId: flatFields.creditCardId,
      recipientFirstName: flatFields.recipientFirstName,
      recipientLastName: flatFields.recipientLastName,
      expenseDetails: Object.entries(expenseDetails)
        .filter((keyVal) => !!keyVal[1])
        .map(([key, val]) => ({ categoryId: key, labelId: val })),
      displayName: flatFields.displayName,
      balanceCents:
        flatFields.balanceDollars !== undefined
          ? toCents(flatFields.balanceDollars)
          : undefined,
      currency: flatFields.currency,
      notes: flatFields.notes,
      validTo:
        flatFields.validToMoment !== undefined
          ? flatFields.validToMoment.format('YYYY-MM-DD')
          : undefined,
      receiptAttachmentIds: props.receiptAttachments.map((att) => att.id),
      direct: true,
      address1: flatFields.customBillingAddress
        ? customAddressFields.address1
        : undefined,
      address2: flatFields.customBillingAddress
        ? customAddressFields.address2
        : undefined,
      city: flatFields.customBillingAddress
        ? customAddressFields.city
        : undefined,
      province: flatFields.customBillingAddress
        ? customAddressFields.province
        : undefined,
      postal: flatFields.customBillingAddress
        ? customAddressFields.postal
        : undefined,
      country: flatFields.customBillingAddress
        ? customAddressFields.country
        : undefined,
      hasPlasticCard: flatFields.includePhysicalCard,
      plasticFirstName: flatFields.includePhysicalCard
        ? flatFields.plasticFirstName
        : undefined,
      plasticLastName: flatFields.includePhysicalCard
        ? flatFields.plasticLastName
        : undefined,
      plasticAddress1:
        flatFields.includePhysicalCard &&
        flatFields.physicalCustomShippingAddress
          ? physicalAddressFields.address1
          : undefined,
      plasticAddress2:
        flatFields.includePhysicalCard &&
        flatFields.physicalCustomShippingAddress
          ? physicalAddressFields.address2
          : undefined,
      plasticCity:
        flatFields.includePhysicalCard &&
        flatFields.physicalCustomShippingAddress
          ? physicalAddressFields.city
          : undefined,
      plasticProvince:
        flatFields.includePhysicalCard &&
        flatFields.physicalCustomShippingAddress
          ? physicalAddressFields.province
          : undefined,
      plasticPostal:
        flatFields.includePhysicalCard &&
        flatFields.physicalCustomShippingAddress
          ? physicalAddressFields.postal
          : undefined,
      plasticCountry:
        flatFields.includePhysicalCard &&
        flatFields.physicalCustomShippingAddress
          ? physicalAddressFields.country
          : undefined,
      singleExactPay: flatFields.singleExactPay,
    }

    flatFields?.userEmails?.length === 1
      ? props.postVirtualCardAndOpenToastAndCloseFlyout({
          ...fields,
          recipient: flatFields.userEmails[0],
        })
      : props.postBulkVirtualCardAndOpenToastAndCloseFlyout({
          ...fields,
          recipients: flatFields.userEmails,
        })

    analytics?.track('Sucessfully create virtual card', {
      type_of_card: VirtualCardTypes.guestAccess,
      card_name: flatFields.displayName,
      credit_limit: flatFields.balanceDollars,
      active_until: flatFields.validToMoment?.format('YYYY-MM-DD'),
      expense_category: Object.keys(expenseDetails).length > 0,
      add_attachments:
        props.receiptAttachments && props.receiptAttachments.length > 0,
      add_notes: !!flatFields.notes,
      recipient_count: flatFields.userEmails?.length,
      is_recipient_me:
        flatFields.userEmails &&
        user &&
        flatFields.userEmails.includes(user.email),
      issuer:
        creditCards &&
        flatFields.creditCardId &&
        creditCards[flatFields.creditCardId]
          ? creditCards[flatFields.creditCardId].issuer
          : undefined,
    })
  }

  const initialFlatFields = {
    // creditCardId: props.creditCardOptions.length === 1
    //   ? props.creditCardOptions[0].value
    //   : queryParams['creditCardId'] !== undefined && creditCards[queryParams['creditCardId']].features.direct
    //     ? queryParams['creditCardId']
    //     : undefined
    // TODO: refactor all uses of queryParams to handle arrays and normalize to string[]
    creditCardId:
      typeof queryParams.creditCardId === 'string'
        ? queryParams.creditCardId
        : undefined,
  }

  return (
    <ActionRightFlyoutContent
      title={<Trns source={'Create a Virtual Card'} />}
      specificElements={{
        subtitle: <Trns source={'Guest Card'} />,
        imageSrc: guestCardImage,
        changeLink: {
          ariaLabelSource: 'Change Card Type',
          changeMessage: <Trns source={'Change Card Type'} />,
          onChangeClick: (): void => {
            props.setScreen(CreateVirtualCardScreen.Pick)
          },
        },
      }}
    >
      <VirtualCardForm
        buttonText={<Trns source={'Create a Virtual Card'} />}
        multiCardButtonText={<Trns source={'Create Virtual Cards'} />}
        onSuccessfulSubmit={onSuccessfulSubmit}
        userOptions={props.userOptions}
        recentUserOptions={props.recentUserOptions}
        userFieldLabel={<Trns source={'Recipients'} />}
        customAddressEditable
        physicalCardEditable
        recipientNamesEditable={false}
        recurrenceEditable={false}
        amountProps={{
          amountEditable: true,
        }}
        notesEditable
        creditCardEditable
        loading={props.postingVirtualCard}
        initialFlatFields={initialFlatFields}
        creditCardProps={{
          isFormDisabled: props.isFormDisabled,
          creditCardOptions: props.creditCardOptions,
          labelCreditCardsAsAccounts: props.labelCreditCardsAsAccounts,
        }}
        attachmentsProps={{
          receiptAttachments: props.receiptAttachments,
          uploadingAttachmentThumbnails: props.uploadingAttachmentThumbnails,
          uploadAttachments: props.uploadAttachments,
          deleteAttachment: props.deleteAttachment,
        }}
        topLevelError={props.topLevelError}
        errorDetailMap={props.errorDetailMap}
        userInfiniteScrollProps={props.userInfiniteScrollProps}
        creditCardInfiniteScrollProps={props.creditCardInfiniteScrollProps}
        tooltip={
          <RecipientsTooltip
            openLeft={false}
            css={css`
              z-index: 1;
              padding-bottom: 6px;
            `}
          />
        }
        isMulti
        suggestedProps={{
          showSuggested: true,
        }}
        filterUserbyAssocationTypes={[UserAssociation.virtualCardTo]}
        typeofCard={VirtualCardTypes.guestAccess}
        showCardApprovalSoftAlert={props.showSoftAlert}
      />
    </ActionRightFlyoutContent>
  )
}

export default CreateVirtualCardGuestAccessContent
